import styles from './tui-favorites-popup-add-hotel.scss';
import { PopupElement } from './PopupElement.js';
import { translate } from '../../translations/index.js';
import { isMobileScreen } from '../../utils/helper.js';
import { renderEmptyListIcon } from '../../utils/render.js';
import { PopupDisplayMode } from '../../utils/enums.js';

class FavoritesPopupAddHotel extends PopupElement {
  activeList = {};
  data = {
    giataId: '',
    placeholderListName: '',
    searchParams: ''
  };
  locale = '';
  #displayMode = undefined;
  #isUserLoggedIn = false;
  #lists = [];
  #tenant = '';

  constructor() {
    super();
  }

  async connectedCallback() {
    super.connectedCallback();

    this.#tenant = this.getAttribute('tenant');
    this.locale = this.getAttribute('locale');
  }

  /**
   * Sets the popup's lists.
   *
   * @param {Array} lists
   */
  setLists(lists) {
    this.#lists = lists;
  }

  /**
   * Sets the popup's display mode.
   *
   * @param {PopupDisplayMode} displayMode
   */
  setDisplayMode(displayMode) {
    if (displayMode) {
      this.#displayMode = displayMode;
    } else {
      this.#displayMode = this.#lists.length ? PopupDisplayMode.SELECT : PopupDisplayMode.CREATE;
    }
  }

  /**
   * Opens the popup in the set display mode.
   *
   * @param {Object} object settings and data
   * @param {Array} object.position
   * @param {Number} object.width
   * @param {Object} object.data
   * @param {Boolean} object.isUserLoggedIn
   */
  open({ position, width = 300, data, isUserLoggedIn = false }) {
    this.render();

    this.data = data;
    this.#isUserLoggedIn = isUserLoggedIn;

    // Open and render
    super.open(position, width, data);
  }

  render() {
    super.render(`
      <style>${styles}</style>
      <div class="tui-favorites-popup-add-hotel">
        ${this.#displayMode === PopupDisplayMode.CREATE ? this.#renderCreate() : ''}
        ${this.#displayMode === PopupDisplayMode.SELECT ? this.#renderSelect() : ''}
      </div>
    `);
  }

  #renderCreate() {
    const locale = this.locale;

    this.popupTitle = translate('popup.addHotel.title', { locale });

    return `
        <div>
          <div class="tui-favorites-input">
            <input
              class="tui-favorites-input__element"
              type="text"
              name="favorites-popup-list-name"
              value="${this.data.placeholderListName}"
              placeholder="${translate('popup.addHotel.placeholder', { locale })}"
              maxlength="30"
            />
            <button class="tui-favorites-input__clear" data-action="clear">
              <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
                <g fill="none" fill-rule="evenodd" stroke="#b9b9b9" stroke-linecap="round" stroke-linejoin="round" stroke-width="8">
                  <line x1="30.349" x2="89.651" y1="30.349" y2="89.651"/>
                  <line x1="89.651" x2="30.349" y1="30.349" y2="89.651"/>
                </g>
              </svg>
            </button>
          </div>
        </div>

        <button
          class="tui-favorites-popup-add-hotel__button tui-favorites-button tui-favorites-button--primary"
          ${this.isButtonLoading ? 'disabled' : ''}
          data-action="createList">
          <p class="link-button__text">
            ${translate('popup.addHotel.button', { locale })}
          </p>
        </button>
      `;
  }

  #renderAction() {
    if (this.#tenant !== 'TUICOM' && !this.#isUserLoggedIn && this.#lists.length >= 6) {
      return '';
    }

    return `
      <li class="tui-favorites-popup-add-hotel__item">
        <button data-action="popUpAction">
          <div class="tui-favorites-popup-add-hotel__popupicon data-action="popUpAction">
            <img class="plus" src="https://mwa.tui.com/shared/mwa/assets/v2/icons/tui-light/plus.svg" data-action="popUpAction" />
          </div>
          <p class="link-button__text">
            ${translate('popup.addHotel.create', { locale: this.locale })}
          </p>
        </button>
      </li>
    `;
  }

  #renderSelect() {
    this.popupTitle = translate('popup.addHotel.select', { locale: this.locale });

    return `
        <ul class="tui-favorites-popup-add-hotel__lists">
        ${this.#renderAction()}
        ${this.#lists
          .map((list) => {
            const listImage = this.#getPictureByListId(list.listId);

            return `
                <li class="tui-favorites-popup-add-hotel__item">
                  <button
                    data-action="changeList"
                    data-new-list-id="${list.listId}">
                    <div class="tui-favorites-popup-add-hotel__popuppicture">
                      ${listImage ? `<img src="https://cloud.tui.com/pics/favorites/resize:fill:56:56/${btoa(listImage)}" />` : renderEmptyListIcon()}
                    </div>
                    <p class="link-button__text">
                      ${list.listName}
                    </p>
                  </button>
                </li>`;
          })
          .join('')}
        </ul>
      `;
  }

  observe() {
    super.observe();

    this.addEventListener('click', async (event) => {
      const { action } = event.target?.dataset || {};

      if (!action) return;

      const currentDisplayMode = this.#displayMode;

      switch (action) {
        case 'clear':
          event.stopPropagation();

          this.data.placeholderListName = '';

          this.render();
          this.querySelector('input[name=favorites-popup-list-name]')?.focus();

          break;

        case 'createList':
          event.stopPropagation();

          let listName = this.querySelector('input[name=favorites-popup-list-name]')?.value ?? '';
          listName = listName.slice(0, 256);

          this.data.placeholderListName = listName;

          // Loading effect
          this.isButtonLoading = true;

          this.render();

          // Save list
          this.containerNode.dispatchEvent(
            new CustomEvent('tui-favorites-popup.CreateFavoriteList', {
              bubbles: true,
              composed: true,
              detail: {
                listName,
                callback: (listId) => {
                  this.isButtonLoading = false;
                  this.backActions = [];

                  this.close();

                  this.#addEntryToList(listId, this.data.giataId, this.data.searchParams, !this.isSaved);
                }
              }
            })
          );

          break;

        case 'changeList':
          const { newListId } = event.target?.dataset;
          this.#addEntryToList(newListId, this.data.giataId, this.data.searchParams, false);

          this.backActions = [];

          if (!isMobileScreen()) {
            // Only close the popup on desktop screens. On mobile screens this
            // will be handled via the notification component.
            this.close();
          }

          this.activeList = this.#lists.find((list) => list.listId === newListId);
          this.dispatchEvent(
            new CustomEvent('tui-favorites-popup.FavoritesListsChanged', {
              bubbles: true,
              composed: true,
              detail: {
                listName: this.activeList.listName,
                numberOfEntries: this.activeList.entries.length || 0
              }
            })
          );

          break;

        case 'popUpAction':
          event.stopPropagation();

          if (this.#tenant !== 'TUICOM') {
            this.#createList();
            return;
          }

          if (this.#isUserLoggedIn || this.#lists.length < 6) {
            this.#createList();
          } else {
            this.#triggerLogin();
            this.close();
          }

          break;

        case 'pageChangeList':
          event.stopPropagation();

          this.backActions.push(() => {
            this.#displayMode = currentDisplayMode;
            this.render();
          });

          this.#displayMode = PopupDisplayMode.SELECT;

          this.render();
          break;

        default:
          console.warn(`Popup action "${action}" not found`);
      }
    });
  }

  async #addEntryToList(listId, giataId, searchParams, isNewEntry) {
    this.containerNode.dispatchEvent(
      new CustomEvent('tui-favorites-popup.AddEntryToFavoriteList', {
        bubbles: true,
        composed: true,
        detail: {
          giataId,
          listId,
          searchParams,
          isNewEntry
        }
      })
    );
  }

  #createList() {
    this.backActions.push(() => {
      this.#displayMode = currentDisplayMode;
      this.render();
    });

    this.#displayMode = PopupDisplayMode.CREATE;
    this.data.placeholderListName = this.data.placeholderListName || '';

    this.render();
  }

  #getPictureByListId(listId) {
    const url = this.#lists
      .find((list) => list.listId === listId)
      ?.entries?.find(() => true)
      ?.hotelContent?.images?.find(() => true)?.url;

    return url;
  }

  #triggerLogin() {
    this.dispatchEvent(
      new CustomEvent('tui-favorites.TriggerLogin', {
        bubbles: true,
        composed: true
      })
    );
  }
}

customElements.define('tui-favorites-popup-add-hotel', FavoritesPopupAddHotel);
